import { useEffect, useState, useCallback, useRef } from 'react'
import { Card } from '@/components/Card'
import { StatusBadge } from '@/components/StatusBadge'
import { fetchApi } from '@/lib/api'
import { Server, GitBranch, Globe, Bug, RefreshCw, Activity, AlertTriangle, CheckCircle, Clock } from 'lucide-react'
import { formatTime, timeAgo } from '@/lib/date'

type ECSService = {
  name: string
  status: string
  desired: number
  running: number
  pending: number
  taskDefinition: string
  lastDeployment: string | null
}

type EnvData = {
  ecs_services: ECSService[]
  status: string
  error?: string
}

type CICDRepo = {
  repo: string
  status: string
  latest_run: {
    name: string
    conclusion: string
    branch: string
    commit_sha: string
    commit_message: string
    updated_at: string
    html_url: string
  } | null
  error?: string
}

type VercelProject = {
  id: string
  name: string
  framework: string | null
  latest_deployment: {
    uid: string
    name: string
    url: string
    state: string
    target: string | null
    created: number
    commit_message: string
    commit_sha: string
    branch: string
  } | null
}

type VercelData = {
  projects: VercelProject[]
  status: string
  error?: string
}

type SentryProjectCount = {
  project: string
  count: number
}

type SentryData = {
  total_unresolved: number
  by_project: SentryProjectCount[]
  status: string
  error?: string
}

type OverviewData = {
  dev: EnvData
  prod: EnvData
  cicd: CICDRepo[]
  vercel?: VercelData
  sentry?: SentryData
}

const AUTO_REFRESH_INTERVAL = 30_000

export function Dashboard() {
  const [data, setData] = useState<OverviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchApi<OverviewData>('/dashboard/overview')
      setData(result)
      setLastUpdated(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(load, AUTO_REFRESH_INTERVAL)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh, load])

  // Summary stats
  const prodServices = data?.prod?.ecs_services ?? []
  const devServices = data?.dev?.ecs_services ?? []
  const prodHealthy = prodServices.filter(s => s.running === s.desired).length
  const devHealthy = devServices.filter(s => s.running === s.desired).length
  const cicdRepos = Array.isArray(data?.cicd) ? data.cicd : []
  const cicdSuccess = cicdRepos.filter(r => r.status === 'success').length
  const cicdFailed = cicdRepos.filter(r => r.status === 'failure').length
  const vercelProjects = data?.vercel?.projects ?? []
  const vercelReady = vercelProjects.filter(p => p.latest_deployment?.state?.toUpperCase() === 'READY').length
  const vercelError = vercelProjects.filter(p => p.latest_deployment?.state?.toUpperCase() === 'ERROR').length
  const sentryTotal = data?.sentry?.total_unresolved ?? 0

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Dashboard</h2>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-[var(--color-text-muted)]">
              Updated {formatTime(lastUpdated)}
            </span>
          )}
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-3 h-3"
            />
            Auto (30s)
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className="flex items-center gap-2 text-[var(--color-text-muted)] text-xs mb-1">
            <Activity size={12} />
            PROD ECS
          </div>
          <div className="text-2xl font-bold">
            {prodHealthy}/{prodServices.length}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">services healthy</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className="flex items-center gap-2 text-[var(--color-text-muted)] text-xs mb-1">
            <Server size={12} />
            DEV ECS
          </div>
          <div className="text-2xl font-bold">
            {devHealthy}/{devServices.length}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">services healthy</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className="flex items-center gap-2 text-green-500 text-xs mb-1">
            <CheckCircle size={12} />
            CI/CD OK
          </div>
          <div className="text-2xl font-bold text-green-500">{cicdSuccess}</div>
          <div className="text-xs text-[var(--color-text-muted)]">repos passing</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className={`flex items-center gap-2 text-xs mb-1 ${cicdFailed > 0 ? 'text-red-500' : 'text-[var(--color-text-muted)]'}`}>
            <AlertTriangle size={12} />
            CI/CD Failed
          </div>
          <div className={`text-2xl font-bold ${cicdFailed > 0 ? 'text-red-500' : ''}`}>
            {cicdFailed}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">repos failing</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className={`flex items-center gap-2 text-xs mb-1 ${vercelError > 0 ? 'text-red-500' : 'text-[var(--color-text-muted)]'}`}>
            <Globe size={12} />
            Vercel
          </div>
          <div className={`text-2xl font-bold ${vercelError > 0 ? 'text-red-500' : ''}`}>
            {vercelReady}/{vercelProjects.length}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">projects ready</div>
        </div>
        <div className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className={`flex items-center gap-2 text-xs mb-1 ${sentryTotal > 0 ? 'text-orange-500' : 'text-[var(--color-text-muted)]'}`}>
            <Bug size={12} />
            Sentry
          </div>
          <div className={`text-2xl font-bold ${sentryTotal > 0 ? 'text-orange-500' : ''}`}>
            {sentryTotal}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">unresolved issues</div>
        </div>
      </div>

      {/* ECS Services */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(['prod', 'dev'] as const).map((env) => {
          const services = data?.[env]?.ecs_services ?? []
          const unhealthy = services.filter(s => s.running !== s.desired)

          return (
            <Card
              key={env}
              title={`ECS Services - ${env.toUpperCase()}`}
              action={
                data?.[env] && (
                  <StatusBadge status={data[env].status} />
                )
              }
            >
              {loading && !data ? (
                <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
              ) : services.length > 0 ? (
                <div className="space-y-1.5">
                  {/* Show unhealthy first */}
                  {[...unhealthy, ...services.filter(s => s.running === s.desired)].map((svc) => {
                    const healthy = svc.running === svc.desired
                    return (
                      <div
                        key={svc.name}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          healthy ? 'bg-[var(--color-bg)]' : 'bg-red-500/5 border border-red-500/20'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Server size={14} className={healthy ? 'text-[var(--color-text-muted)]' : 'text-red-500'} />
                          <span className="font-mono truncate">{svc.name}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {svc.lastDeployment && (
                            <span className="text-[10px] text-[var(--color-text-muted)] hidden xl:inline">
                              <Clock size={10} className="inline mr-1" />
                              {timeAgo(svc.lastDeployment)}
                            </span>
                          )}
                          <span className={`text-xs font-mono ${healthy ? 'text-[var(--color-text-muted)]' : 'text-red-500 font-bold'}`}>
                            {svc.running}/{svc.desired}
                          </span>
                          <StatusBadge
                            status={healthy ? 'ok' : 'warning'}
                            label={svc.status}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-[var(--color-text-muted)]">
                  {data?.[env]?.error ?? 'No services found'}
                </p>
              )}
            </Card>
          )
        })}
      </div>

      {/* CI/CD Status */}
      <Card title="CI/CD - Latest Runs">
        {loading && !data ? (
          <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
        ) : cicdRepos.length > 0 ? (
          <div className="space-y-1.5">
            {cicdRepos.map((repo) => (
              <div
                key={repo.repo}
                className={`flex items-center justify-between p-2 rounded text-sm ${
                  repo.status === 'failure' ? 'bg-red-500/5 border border-red-500/20' : 'bg-[var(--color-bg)]'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <GitBranch size={14} className="text-[var(--color-text-muted)] shrink-0" />
                  <span className="font-mono shrink-0">{repo.repo}</span>
                  {repo.latest_run && (
                    <span className="text-xs text-[var(--color-text-muted)] truncate hidden md:inline">
                      {repo.latest_run.commit_message}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {repo.latest_run && (
                    <>
                      <span className="text-[10px] text-[var(--color-text-muted)] hidden lg:inline">
                        {repo.latest_run.branch}
                      </span>
                      <span className="text-xs font-mono text-[var(--color-text-muted)]">
                        {repo.latest_run.commit_sha}
                      </span>
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        {timeAgo(repo.latest_run.updated_at)}
                      </span>
                    </>
                  )}
                  <StatusBadge status={repo.status ?? 'unknown'} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">Failed to load CI/CD data</p>
        )}
      </Card>

      {/* Sentry Issues */}
      {(data?.sentry?.by_project ?? []).length > 0 && (
        <Card title="Sentry - Unresolved Issues by Project">
          <div className="space-y-1.5">
            {(data?.sentry?.by_project ?? []).map((item) => (
              <div
                key={item.project}
                className="flex items-center justify-between p-2 rounded text-sm bg-[var(--color-bg)]"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Bug size={14} className="text-[var(--color-text-muted)] shrink-0" />
                  <span className="font-mono">{item.project}</span>
                </div>
                <span className={`text-sm font-mono ${item.count > 0 ? 'text-orange-400' : 'text-[var(--color-text-muted)]'}`}>
                  {item.count} issues
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Vercel Deployments */}
      <Card title="Vercel - Latest Deployments">
        {loading && !data ? (
          <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
        ) : vercelProjects.length > 0 ? (
          <div className="space-y-1.5">
            {vercelProjects.map((project) => {
              const d = project.latest_deployment
              const state = d?.state?.toUpperCase() ?? 'UNKNOWN'
              const statusKey = state === 'READY' ? 'ok' : state === 'ERROR' ? 'error' : state === 'BUILDING' ? 'pending' : 'unknown'
              return (
                <div
                  key={project.id}
                  className={`flex items-center justify-between p-2 rounded text-sm ${
                    state === 'ERROR' ? 'bg-red-500/5 border border-red-500/20' : 'bg-[var(--color-bg)]'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Globe size={14} className="text-[var(--color-text-muted)] shrink-0" />
                    <span className="font-mono shrink-0">{project.name}</span>
                    {d?.commit_message && (
                      <span className="text-xs text-[var(--color-text-muted)] truncate hidden md:inline">
                        {d.commit_message}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {d && (
                      <>
                        {d.branch && (
                          <span className="text-[10px] text-[var(--color-text-muted)] hidden lg:inline">
                            {d.branch}
                          </span>
                        )}
                        {d.commit_sha && (
                          <span className="text-xs font-mono text-[var(--color-text-muted)]">
                            {d.commit_sha}
                          </span>
                        )}
                        {d.created && (
                          <span className="text-[10px] text-[var(--color-text-muted)]">
                            {timeAgo(new Date(d.created).toISOString())}
                          </span>
                        )}
                      </>
                    )}
                    <StatusBadge status={statusKey} label={state} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            {data?.vercel?.error ?? 'No Vercel projects found'}
          </p>
        )}
      </Card>
    </div>
  )
}
